// i18n configuration. Portuguese is the canonical (default) locale and
// lives at the URL root with no prefix; English and Spanish are served
// under /en and /es. The locale is decided by the route (so it works
// with static pre-rendering), not by a runtime detector.

export const LOCALES = ["pt", "en", "es"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "pt";

export const LOCALE_LABELS: Record<Locale, string> = {
  pt: "PT",
  en: "EN",
  es: "ES",
};

// hreflang codes for <link rel="alternate"> and og:locale.
export const LOCALE_HREFLANG: Record<Locale, string> = {
  pt: "pt-BR",
  en: "en",
  es: "es",
};

export const SITE_ORIGIN = "https://truehacking.ai";

// Page keys are locale-agnostic ("" = home). Each maps to a localized
// URL slug per locale, so the path itself is translated for SEO.
export type PageKey = "platform" | "pricing" | "privacy" | "terms" | "blog";

export const PAGE_SLUGS: Record<PageKey, Record<Locale, string>> = {
  platform: { pt: "plataforma", en: "platform", es: "plataforma" },
  pricing: { pt: "precos", en: "pricing", es: "precios" },
  privacy: { pt: "privacidade", en: "privacy", es: "privacidad" },
  terms: { pt: "termos", en: "terms", es: "terminos" },
  // Blog uses the same slug across locales (kept simple); only the
  // /<locale> prefix differs.
  blog: { pt: "blog", en: "blog", es: "blog" },
};

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

// URL path for a page on a locale. `page` is a PageKey ("" for home).
// The slug is localized; the non-default locale gets a /<locale> prefix.
// e.g. localePath("pt","pricing") -> /precos ; ("es","pricing") -> /es/precios
export function localePath(locale: Locale, page: PageKey | "" = ""): string {
  const prefix = locale === DEFAULT_LOCALE ? "" : `/${locale}`;
  const slug = page ? PAGE_SLUGS[page][locale] : "";
  const suffix = slug ? `/${slug}` : "";
  const path = `${prefix}${suffix}`;
  return path === "" ? "/" : path;
}

export function localeUrl(locale: Locale, page: PageKey | "" = ""): string {
  const path = localePath(locale, page);
  return `${SITE_ORIGIN}${path === "/" ? "/" : path}`;
}
